import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';

/**
 * Thin bar at the top of the page while the router is loading the next page
 * (downloading its code, running guards). Appears only if loading takes longer
 * than 150 ms, so fast page changes don't flicker.
 */
@Component({
  selector: 'app-route-progress',
  standalone: true,
  template: `<div class="route-progress" [class.is-active]="active" role="progressbar"
               [attr.aria-hidden]="!active" aria-label="Loading page"></div>`,
  styles: [`
    .route-progress {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      z-index: 4000;
      pointer-events: none;
      opacity: 0;
      overflow: hidden;
      transition: opacity 0.2s ease;
    }
    .route-progress::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 35%;
      left: -35%;
      background: var(--accent, #ffc700);
      box-shadow: 0 0 10px rgba(var(--accent-rgb, 255, 199, 0), 0.7);
      border-radius: 3px;
    }
    .route-progress.is-active {
      opacity: 1;
    }
    .route-progress.is-active::before {
      animation: route-progress-slide 1s ease-in-out infinite;
    }
    @keyframes route-progress-slide {
      0% { left: -35%; }
      100% { left: 100%; }
    }
    @media (prefers-reduced-motion: reduce) {
      .route-progress.is-active::before { animation: none; left: 0; width: 100%; }
    }
  `],
})
export class RouteProgressComponent {
  active = false;
  private showTimer: any = null;

  constructor() {
    const router = inject(Router);
    router.events.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe(event => {
      if (event instanceof NavigationStart) {
        clearTimeout(this.showTimer);
        this.showTimer = setTimeout(() => (this.active = true), 150);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        clearTimeout(this.showTimer);
        this.active = false;
      }
    });
  }
}
